import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentActions, usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import { addDatadogError } from "../../services/datadog";
import { recordPrint } from "../../services/printHistory";
import { parseTerminalError } from "../terminalError";
import useGetOrderDetail from "../useGetOrderDetail/useGetOrderDetail";
import usePostPrint from "../usePostPrint/usePostPrint";
import { PAYMENT_ERROR_CODES, PAYMENT_METHODS } from "./types";

const PAYMENT_HOST: string = process.env.TERMINAL_HOST ?? "";
const PAYMENT_END_POINT: string = process.env.API_PAYMENT ?? "";
const PAYMENT_TIMEOUT_MS = 300000;

/**
 * One customer tap on a payment method produces exactly one of these. The
 * object identity is the deduplication key for the POST — see the effect that
 * issues the request.
 *
 * `token` is captured here, at tap time, and is deliberately NOT re-read from
 * `user.session_id` later. The terminal transaction is authorised under the
 * session that started it, and the token travels twice: in the `Authorization`
 * header and in the body as `token`. Holding one captured value and using it
 * for both is what keeps the two from disagreeing when the session is replaced
 * while the payment is in flight.
 */
interface IPaymentRequest {
	url: string;
	body: Record<string, unknown>;
	token: string;
}

const PAYMENT_API_METHOD_MAP: Partial<Record<PAYMENT_METHODS, PAYMENT_METHODS>> = {
	[PAYMENT_METHODS.ALIPAY_WECHAT]: PAYMENT_METHODS.NETSQR,
};

const useSubmitPayment = () => {
	const navigate = useNavigate();
	const {
		state: { user },
	} = useConfigContext((state) => state);
	const {
		state: { paymentReference, paymentData },
	} = usePaymentContext((state) => state);
	const { SET_ORDER_STATUS } = usePaymentActions();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isChargedButFailed, setIsChargedButFailed] = useState(false);
	const [paymentRequest, setPaymentRequest] = useState<IPaymentRequest | null>(null);
	const [currentMethod, setCurrentMethod] = useState<PAYMENT_METHODS | null>(null);
	const paymentProcessedRef = useRef(false);
	const dispatchedPaymentRequestRef = useRef<IPaymentRequest | null>(null);

	// Correctly use useGetOrderDetail hook to fetch order details
	const { fetchOrderDetail } = useGetOrderDetail({
		autoFetch: false,
	});

	// Use print hook for receipt printing
	const { submitPrint } = usePostPrint();
	// Setup for Payment Submission API call
	const paymentPayload = useMemo(
		() => ({
			url: paymentRequest?.url ?? "",
			triggerOnMount: false,
			axiosConfig: addJWTInConfig(
				{ timeout: PAYMENT_TIMEOUT_MS },
				user?.session_id?.toString() ?? ""
			),
			body: paymentRequest?.body,
			swrConfig: {
				revalidateOnFocus: false,
				revalidateOnReconnect: false,
				revalidateOnMount: false,
				revalidateIfStale: false,
				dedupingInterval: 0,
				refreshInterval: 0,
			},
		}),
		[paymentRequest, paymentRequest?.body, paymentRequest?.url, user?.session_id, paymentReference]
	);

	const {
		data: paymentResponse,
		error: paymentError,
		postData,
	} = useApiRequestPost(paymentPayload);

	// 1. Main function to be called by the UI
	const submitPayment = useCallback(
		(paymentMethod: PAYMENT_METHODS) => {
			if (isLoading) return;

			paymentProcessedRef.current = false; // Reset flag on new payment attempt
			setCurrentMethod(paymentMethod);
			setIsLoading(true);
			setError(null);

			const apiMethod = PAYMENT_API_METHOD_MAP[paymentMethod] ?? paymentMethod;
			const url = `${PAYMENT_HOST}${PAYMENT_END_POINT}/${apiMethod}?paymentReference=${paymentReference}`;
			// Snapshot of the session token as it stands at the moment of the tap.
			// Reused for both the body and the Authorization header when the request
			// goes out, so the two can never carry different values.
			const token = user?.session_id?.toString() ?? "";
			const body = {
				referenceNumber: paymentReference,
				amount: paymentData?.orders_figures.net_value ?? 0,
				token,
			};

			// A fresh object every time. Its identity is what the issuing effect
			// deduplicates on, which is also why retrying after a failure still
			// produces a new POST.
			setPaymentRequest({ url, body, token });
		},

		[paymentData, paymentReference, isLoading, user?.session_id]
	);

	// 2. Effect to trigger the payment POST request when paymentRequest is set
	useEffect(() => {
		if (!paymentRequest) return;

		// This effect re-runs for reasons that have nothing to do with the customer
		// wanting to pay again. `postData` is rebuilt whenever the axiosConfig it
		// closes over is rebuilt, and that config is rebuilt on every change of
		// `user.session_id` — a session refetch or a JWT refresh landing mid-payment
		// is enough. `paymentRequest` is still set at that point, so the naive
		// version fired a second POST against a terminal that was still working on
		// the first one, under the same paymentReference.
		//
		// Deduplicating on the identity of the request object gives one POST per
		// `submitPayment` call regardless of how often this effect re-runs, while
		// still letting a retry through: `submitPayment` always allocates a new
		// object, which never matches the one already dispatched.
		if (dispatchedPaymentRequestRef.current === paymentRequest) return;

		dispatchedPaymentRequestRef.current = paymentRequest;

		// Send the captured token, not the current one. Passing the body and the
		// header config explicitly pins both to the same snapshot taken at tap time,
		// rather than to whatever the latest render happened to memoise.
		//
		// The override starts from an explicit, empty `headers` object on purpose:
		// addJWTInConfig leaves the config untouched for an empty token, and
		// useApiRequestPost spreads the override over the memoised axiosConfig, so
		// without a `headers` key of its own the memoised Authorization header —
		// built from the *current* session — would survive the merge and the POST
		// would carry a header token that the (empty) body token does not have.
		postData(
			paymentRequest.body,
			addJWTInConfig({ timeout: PAYMENT_TIMEOUT_MS, headers: {} }, paymentRequest.token)
		).catch((err) => {
			console.log("🔍 PostData promise rejected in useEffect:", err);
			// The error should be handled by SWR, but if it's not, handle it here
			if (!paymentError) {
				setError(err?.message || "Payment request failed");
				setIsLoading(false);
				setCurrentMethod(null);
				setPaymentRequest(null);
			}
		});
	}, [paymentRequest, postData, paymentError]);

	// 3. Combined effect to handle the entire async flow after payment submission
	useEffect(() => {
		const handlePaymentSuccess = async () => {
			if (paymentProcessedRef.current) return;

			// Double check: don't call order endpoint if there's any payment error
			if (paymentError) {
				console.log("🔍 Payment error detected in handlePaymentSuccess, aborting order call");

				return;
			}

			// Final validation of payment response
			if (
				!paymentResponse ||
				(paymentResponse.status !== "success" &&
					paymentResponse.success !== true &&
					paymentResponse.success !== "true")
			) {
				console.log("🔍 Payment response validation failed, aborting order call:", paymentResponse);
				setError("Payment validation failed");
				setIsLoading(false);
				setCurrentMethod(null);

				return;
			}

			paymentProcessedRef.current = true;
			SET_ORDER_STATUS("loading");
			try {
				// SWR will handle the retries automatically (5 attempts, 1 second interval)
				const result = await fetchOrderDetail(paymentReference ?? undefined);

				if (result) {
					// The status will be set to 'success' inside useGetOrderDetail

					// Print customer receipt first, then kitchen stubs, in a single
					// sequential batch. submitPrint serialises its input array
					// internally (see usePostPrint: for-loop + 800ms gap), so a
					// single combined array prevents parallel dispatch from
					// interleaving printout data on the non-concurrent print server.
					const stubs = result?.sok_receipt_stubs ?? [];
					const snapshot = result?.sok_receipt_snapshot ?? [];
					const printPayload = [...snapshot, ...stubs];

					try {
						await submitPrint(printPayload);
						recordPrint({
							amountCents: result?.order_items?.orders_figures?.net_value ?? 0,
							type: "receipt",
							payload: printPayload,
						});
					} catch (err) {
						addDatadogError(err instanceof Error ? err : new Error("Receipt print failed"), {
							paymentReference,
							stubCount: stubs.length,
							snapshotCount: snapshot.length,
						});
					}

					navigate(PATH.ORDER_CONFIRMATION);
				} else {
					SET_ORDER_STATUS("fail");
					setError("Failed to retrieve order details after payment.");
				}
			} catch (e: any) {
				SET_ORDER_STATUS("fail");
				setError(e.message || "An error occurred while fetching order details.");
			} finally {
				setIsLoading(false);
				setCurrentMethod(null);
			}
		};

		console.log("🔍 Payment Effect Debug - paymentError:", paymentError);
		console.log("🔍 Payment Effect Debug - paymentResponse:", paymentResponse);

		// Handle error first - if there's an error, don't process success
		if (paymentError) {
			console.log("🔍 Setting error from paymentError:", paymentError?.message || "Payment failed");
			setError(paymentError?.message || "Payment failed");
			setIsLoading(false);
			setCurrentMethod(null);
			setPaymentRequest(null);
			// Reset the processed flag to prevent stale success handling
			paymentProcessedRef.current = false;

			return; // Exit early to prevent success handling
		}

		// Only handle success if there's no error AND payment response indicates success
		if (paymentResponse && !paymentError) {
			console.log("🔍 Checking payment response status:", paymentResponse);

			// Check if the response body contains success status
			const isPaymentSuccessful =
				paymentResponse?.status === "success" ||
				paymentResponse?.success === true ||
				paymentResponse?.success === "true";

			if (isPaymentSuccessful) {
				console.log("🔍 Payment response indicates success, handling payment success");
				handlePaymentSuccess();
			} else {
				console.log("🔍 Payment response indicates failure, treating as error:", paymentResponse);
				if (
					paymentResponse?.error_code === PAYMENT_ERROR_CODES.SOK_PAYMENT_UPDATE_FAILED ||
					paymentResponse?.charged === true
				) {
					setIsChargedButFailed(true);
				} else {
					const errorDetail = parseTerminalError(paymentResponse ?? {})?.detail;

					setError(paymentResponse?.message || errorDetail || "Payment failed - invalid status");
				}
				setIsLoading(false);
				setCurrentMethod(null);
				setPaymentRequest(null);
				paymentProcessedRef.current = false;
			}
		}
	}, [
		paymentResponse,
		paymentError,
		fetchOrderDetail,
		navigate,
		paymentReference,
		SET_ORDER_STATUS,
	]);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	const clearChargedError = useCallback(() => {
		setIsChargedButFailed(false);
	}, []);

	return {
		isLoading,
		error,
		isChargedButFailed,
		submitPayment,
		clearError,
		clearChargedError,
		currentMethod,
	};
};

export default useSubmitPayment;
